import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { config } from '../config';

@Injectable({
  providedIn: 'root'
})
export class ActivationApiService {
  private apiUrl = config.baseUrl + 'supersee/v1/onboarding/v2';

  constructor(private http: HttpClient) {}

  /**
   * Fetches the current V2 onboarding state including steps, progress, and test employee data.
   */
  getV2State(): Observable<any> {
    return this.http.get(`${this.apiUrl}/state`);
  }

  /**
   * Updates the status or data of a specific onboarding step.
   * @param stepId e.g., 'mode_selection', 'create_employee', 'install_agent', 'request_screenshot'
   * @param status 'pending', 'in_progress', 'completed', 'skipped'
   * @param data Optional data/metadata to merge
   */
  updateV2State(stepId: string, status: string, data?: any, metadata?: any): Observable<any> {
    const payload: any = { step_id: stepId, status };
    if (data) payload.data = data;
    if (metadata) payload.metadata = metadata;
    return this.http.post(`${this.apiUrl}/state`, payload);
  }

  /**
   * Creates or reuses the temporary test employee.
   * @param resetPassword Set to true to generate a new temporary password
   */
  createTestEmployee(resetPassword = false): Observable<any> {
    return this.http.post(`${this.apiUrl}/test-employee`, { reset_password: resetPassword });
  }

  /**
   * Fetches full employee details from the standard users API.
   * @param id Employee ID
   */
  getUserById(id: string | number): Observable<any> {
    return this.http.get(`${config.baseUrl}wp/v2/users/${id}`);
  }

  /**
   * Tracks magic link events for mobile handoff.
   * @param event 'sent', 'opened', 'verified'
   */
  trackMobileHandoff(
    event: 'sent' | 'opened' | 'verified', 
    related_employee_id?: number, 
    login_code?: number
  ): Observable<any> {
    const payload: any = { event };
    if (related_employee_id) payload.related_employee_id = related_employee_id;
    if (login_code) payload.login_code = login_code;
    
    return this.http.post(`${this.apiUrl}/mobile-handoff`, payload);
  }

  /**
   * Finalizes the onboarding flow permanently.
   */
  completeV2Flow(): Observable<any> {
    return this.http.post(`${this.apiUrl}/complete`, { source: 'dashboard_button' });
  }

  /**
   * Saves a cleanup decision for the dummy test employee data.
   * @param decision 'keep', 'remove', 'decide_later'
   */
  cleanup(decision: 'keep' | 'remove' | 'decide_later'): Observable<any> {
    return this.http.post(`${this.apiUrl}/cleanup`, { decision });
  }

  // --- Local Storage Helpers ---
  private readonly TEST_EMP_KEY = 'supersee_v2_test_employee';
  private readonly testEmployeeSubject =
    new BehaviorSubject<{ employee: any, password?: string } | null>(this.getTestEmployeeLocally());
  readonly testEmployee$ = this.testEmployeeSubject.asObservable();

  saveTestEmployeeLocally(employee: any, password?: string): void {
    const testEmployeeData = { employee, password };
    try {
      localStorage.setItem(this.TEST_EMP_KEY, JSON.stringify(testEmployeeData));
    } catch (e) {
      console.error('Error saving test employee to local storage', e);
    }
    this.testEmployeeSubject.next(testEmployeeData);
  }

  getTestEmployeeLocally(): { employee: any, password?: string } | null {
    try {
      const data = localStorage.getItem(this.TEST_EMP_KEY);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error('Error reading test employee from local storage', e);
      return null;
    }
  }

  clearTestEmployeeLocally(): void {
    localStorage.removeItem(this.TEST_EMP_KEY);
    this.testEmployeeSubject.next(null);
  }
}
